import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { PlusCircle, Edit, Trash2, Save, X, Play, Pause, Square, Users, Copy, GripVertical } from 'lucide-react';
import { NotificationManager } from './NotificationManager';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Question {
  id: string;
  body: string;
  choices: string[];
  correctIndex: number;
  order: number;
}

interface QuestionSet {
  id: string;
  title: string;
  description?: string;
  gameMode?: 'synchronized' | 'self-paced';
  state: 'open' | 'active' | 'closed';
  questionCount: number;
  questions: Question[];
}

interface PollOption {
  id: string;
  emoji: string;
  label?: string;
}

interface Poll {
  id: string;
  title: string;
  question: string;
  options: PollOption[];
  state: 'open' | 'active' | 'closed';
  stayActive: boolean;
  createdBy: string;
  createdAt: string;
}

interface GuessQuestion {
  id: string;
  question: string;
  correctAnswers: string[];
  caseSensitive: boolean;
  order: number;
}

interface GuessTheValue {
  id: string;
  title: string;
  description: string;
  correctAnswer?: string; // Backward compatibility
  caseSensitive?: boolean; // Backward compatibility
  questions?: GuessQuestion[]; // New: array of questions
  questionCount?: number; // New: track number of questions
  mode: 'multiple' | 'collect';
  state: 'open' | 'active' | 'closed';
  createdBy: string;
  createdAt: string;
}

type StateType = 'open' | 'active' | 'closed';

const stateColors = {
  open: 'bg-blue-100 text-blue-800 border-blue-200',
  active: 'bg-green-100 text-green-800 border-green-200',
  closed: 'bg-gray-100 text-gray-800 border-gray-200'
};

const stateIcons = {
  open: Users,
  active: Play,
  closed: Square
};

// Sortable wrapper component for drag-and-drop
interface SortableCardProps {
  id: string;
  children: React.ReactNode;
}

function SortableCard({ id, children }: SortableCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-2 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing z-10 p-2 hover:bg-gray-100 rounded"
      >
        <GripVertical className="w-5 h-5 text-gray-400" />
      </div>
      {/* Content with left padding to make room for drag handle */}
      <div className="pl-10">
        {children}
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [guessGames, setGuessGames] = useState<GuessTheValue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('trivia');

  // Question Set states
  const [editingSet, setEditingSet] = useState<string | null>(null);
  const [showNewSetForm, setShowNewSetForm] = useState(false);
  const [newSetTitle, setNewSetTitle] = useState('');
  const [newSetDescription, setNewSetDescription] = useState('');
  const [newSetGameMode, setNewSetGameMode] = useState<'synchronized' | 'self-paced'>('synchronized');
  const [editSetTitle, setEditSetTitle] = useState('');
  const [editSetDescription, setEditSetDescription] = useState('');
  const [showNewQuestionForm, setShowNewQuestionForm] = useState<string | null>(null);

  // Poll states
  const [editingPoll, setEditingPoll] = useState<string | null>(null);
  const [showNewPollForm, setShowNewPollForm] = useState(false);
  const [newPollTitle, setNewPollTitle] = useState('');
  const [newPollQuestion, setNewPollQuestion] = useState('');
  const [newPollOptions, setNewPollOptions] = useState<PollOption[]>([
    { id: '1', emoji: '👍', label: 'Yes' },
    { id: '2', emoji: '👎', label: 'No' }
  ]);
  const [newPollStayActive, setNewPollStayActive] = useState(false);

  // Guess the Value states
  const [showNewGuessForm, setShowNewGuessForm] = useState(false);
  const [newGuessTitle, setNewGuessTitle] = useState('');
  const [newGuessDescription, setNewGuessDescription] = useState('');
  const [newGuessAnswer, setNewGuessAnswer] = useState(''); // Keep for backward compat
  const [newGuessCaseSensitive, setNewGuessCaseSensitive] = useState(false); // Keep for backward compat
  const [newGuessMode, setNewGuessMode] = useState<'multiple' | 'collect'>('multiple');
  const [editingGuessGame, setEditingGuessGame] = useState<string | null>(null);
  const [showNewGuessQuestionForm, setShowNewGuessQuestionForm] = useState<string | null>(null);
  
  // Guess Question form states
  const [guessQuestionText, setGuessQuestionText] = useState('');
  const [guessCorrectAnswers, setGuessCorrectAnswers] = useState<string[]>(['']);
  const [guessQuestionCaseSensitive, setGuessQuestionCaseSensitive] = useState(false);
  const [editingGuessQuestion, setEditingGuessQuestion] = useState<string | null>(null);

  // Question form states
  const [questionBody, setQuestionBody] = useState('');
  const [questionChoices, setQuestionChoices] = useState(['', '', '', '']);
  const [correctChoice, setCorrectChoice] = useState(0);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([fetchQuestionSets(), fetchPolls(), fetchGuessGames()]);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuestionSets = async () => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/question-sets`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch question sets');
      }

      const data = await response.json();
      setQuestionSets(data.questionSets || []);
    } catch (err) {
      console.error('Error fetching question sets:', err);
      throw err;
    }
  };

  const fetchPolls = async () => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/polls`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch polls');
      }

      const data = await response.json();
      setPolls(data.polls || []);
    } catch (err) {
      console.error('Error fetching polls:', err);
      // Don't throw here as polls might not be implemented yet
      setPolls([]);
    }
  };

  const fetchGuessGames = async () => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch guess games');
      }

      const data = await response.json();
      setGuessGames(data.guessGames || []);
    } catch (err) {
      console.error('Error fetching guess games:', err);
      // Don't throw here as guess games might not be implemented yet
      setGuessGames([]);
    }
  };

  // Drag and drop handlers
  const handleQuestionSetDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = questionSets.findIndex((item) => item.id === active.id);
    const newIndex = questionSets.findIndex((item) => item.id === over.id);

    // Optimistically update UI
    const reorderedItems = arrayMove(questionSets, oldIndex, newIndex);
    setQuestionSets(reorderedItems);

    // Prepare order updates for backend
    const orderUpdates = reorderedItems.map((item, index) => ({
      id: item.id,
      order: index,
    }));

    // Call backend
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderUpdates }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder question sets');
      }
    } catch (err) {
      console.error('Error reordering question sets:', err);
      setError('Failed to reorder question sets. Please try again.');
      // Revert on error
      await fetchQuestionSets();
    }
  };

  const handlePollDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = polls.findIndex((item) => item.id === active.id);
    const newIndex = polls.findIndex((item) => item.id === over.id);

    // Optimistically update UI
    const reorderedItems = arrayMove(polls, oldIndex, newIndex);
    setPolls(reorderedItems);

    // Prepare order updates for backend
    const orderUpdates = reorderedItems.map((item, index) => ({
      id: item.id,
      order: index,
    }));

    // Call backend
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/polls/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderUpdates }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder polls');
      }
    } catch (err) {
      console.error('Error reordering polls:', err);
      setError('Failed to reorder polls. Please try again.');
      // Revert on error
      await fetchPolls();
    }
  };

  const handleGuessGameDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = guessGames.findIndex((item) => item.id === active.id);
    const newIndex = guessGames.findIndex((item) => item.id === over.id);

    // Optimistically update UI
    const reorderedItems = arrayMove(guessGames, oldIndex, newIndex);
    setGuessGames(reorderedItems);

    // Prepare order updates for backend
    const orderUpdates = reorderedItems.map((item, index) => ({
      id: item.id,
      order: index,
    }));

    // Call backend
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderUpdates }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder guess games');
      }
    } catch (err) {
      console.error('Error reordering guess games:', err);
      setError('Failed to reorder guess games. Please try again.');
      // Revert on error
      await fetchGuessGames();
    }
  };

  const updateQuestionSetState = async (id: string, newState: StateType) => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/${id}/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: newState }),
      });

      if (!response.ok) {
        throw new Error('Failed to update question set state');
      }

      await fetchQuestionSets();
      setError('');
    } catch (err) {
      console.error('Error updating question set state:', err);
      setError('Failed to update question set state. Please try again.');
    }
  };

  const updatePollState = async (id: string, newState: StateType) => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/polls/${id}/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: newState }),
      });

      if (!response.ok) {
        throw new Error('Failed to update poll state');
      }

      await fetchPolls();
      setError('');
    } catch (err) {
      console.error('Error updating poll state:', err);
      setError('Failed to update poll state. Please try again.');
    }
  };

  const createQuestionSet = async () => {
    if (!newSetTitle.trim()) {
      setError('Question set title is required');
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newSetTitle,
          description: newSetDescription,
          gameMode: newSetGameMode,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create question set');
      }

      await fetchQuestionSets();
      setShowNewSetForm(false);
      setNewSetTitle('');
      setNewSetDescription('');
      setNewSetGameMode('synchronized');
      setError('');
    } catch (err) {
      console.error('Error creating question set:', err);
      setError('Failed to create question set. Please try again.');
    }
  };

  const updateQuestionSet = async (id: string) => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: editSetTitle,
          description: editSetDescription,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update question set');
      }

      await fetchQuestionSets();
      setEditingSet(null);
      setError('');
    } catch (err) {
      console.error('Error updating question set:', err);
      setError('Failed to update question set. Please try again.');
    }
  };

  const deleteQuestionSet = async (id: string) => {
    if (!confirm('Are you sure you want to delete this question set? This action cannot be undone.')) {
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete question set');
      }

      await fetchQuestionSets();
      setError('');
    } catch (err) {
      console.error('Error deleting question set:', err);
      setError('Failed to delete question set. Please try again.');
    }
  };

  const duplicateQuestionSet = async (id: string) => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/${id}/duplicate`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate question set');
      }

      await fetchQuestionSets();
      setError('');
    } catch (err) {
      console.error('Error duplicating question set:', err);
      setError('Failed to duplicate question set. Please try again.');
    }
  };

  const startEditingSet = (questionSet: QuestionSet) => {
    setEditingSet(questionSet.id);
    setEditSetTitle(questionSet.title);
    setEditSetDescription(questionSet.description || '');
  };

  const cancelEditingSet = () => {
    setEditingSet(null);
    setEditSetTitle('');
    setEditSetDescription('');
  };

  const createPoll = async () => {
    if (!newPollTitle.trim() || !newPollQuestion.trim()) {
      setError('Poll title and question are required');
      return;
    }

    if (newPollOptions.some(opt => !opt.emoji.trim())) {
      setError('All poll options must have an emoji');
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/polls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newPollTitle,
          question: newPollQuestion,
          options: newPollOptions,
          stayActive: newPollStayActive,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create poll');
      }

      await fetchPolls();
      setShowNewPollForm(false);
      resetPollForm();
      setError('');
    } catch (err) {
      console.error('Error creating poll:', err);
      setError('Failed to create poll. Please try again.');
    }
  };

  const updatePoll = async (id: string) => {
    if (!newPollTitle.trim() || !newPollQuestion.trim()) {
      setError('Poll title and question are required');
      return;
    }

    if (newPollOptions.some(opt => !opt.emoji.trim())) {
      setError('All poll options must have an emoji');
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/polls/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newPollTitle,
          question: newPollQuestion,
          options: newPollOptions,
          stayActive: newPollStayActive,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update poll');
      }

      await fetchPolls();
      setEditingPoll(null);
      resetPollForm();
      setError('');
    } catch (err) {
      console.error('Error updating poll:', err);
      setError('Failed to update poll. Please try again.');
    }
  };

  const deletePoll = async (id: string) => {
    if (!confirm('Are you sure you want to delete this poll? This action cannot be undone.')) {
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/polls/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete poll');
      }

      await fetchPolls();
      setError('');
    } catch (err) {
      console.error('Error deleting poll:', err);
      setError('Failed to delete poll. Please try again.');
    }
  };

  const duplicatePoll = async (id: string) => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/polls/${id}/duplicate`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate poll');
      }

      await fetchPolls();
      setError('');
    } catch (err) {
      console.error('Error duplicating poll:', err);
      setError('Failed to duplicate poll. Please try again.');
    }
  };

  const startEditingPoll = (poll: Poll) => {
    setEditingPoll(poll.id);
    setNewPollTitle(poll.title);
    setNewPollQuestion(poll.question);
    setNewPollOptions(poll.options);
    setNewPollStayActive(poll.stayActive);
  };

  const cancelEditingPoll = () => {
    setEditingPoll(null);
    resetPollForm();
  };

  const createGuessGame = async () => {
    if (!newGuessTitle.trim()) {
      setError('Title is required');
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newGuessTitle,
          description: newGuessDescription || '',
          mode: newGuessMode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create guess game' }));
        throw new Error(errorData.error || 'Failed to create guess game');
      }

      await fetchGuessGames();
      setShowNewGuessForm(false);
      resetGuessForm();
      setError('');
    } catch (err) {
      console.error('Error creating guess game:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create guess game. Please try again.';
      setError(errorMessage);
    }
  };

  const updateGuessGameState = async (id: string, newState: StateType) => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games/${id}/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: newState }),
      });

      if (!response.ok) {
        throw new Error('Failed to update guess game state');
      }

      await fetchGuessGames();
      setError('');
    } catch (err) {
      console.error('Error updating guess game state:', err);
      setError('Failed to update guess game state. Please try again.');
    }
  };

  const deleteGuessGame = async (id: string) => {
    if (!confirm('Are you sure you want to delete this guess game? This action cannot be undone.')) {
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete guess game');
      }

      await fetchGuessGames();
      setError('');
    } catch (err) {
      console.error('Error deleting guess game:', err);
      setError('Failed to delete guess game. Please try again.');
    }
  };

  const duplicateGuessGame = async (id: string) => {
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games/${id}/duplicate`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate guess game');
      }

      await fetchGuessGames();
      setError('');
    } catch (err) {
      console.error('Error duplicating guess game:', err);
      setError('Failed to duplicate guess game. Please try again.');
    }
  };

  const resetGuessForm = () => {
    setNewGuessTitle('');
    setNewGuessDescription('');
    setNewGuessAnswer('');
    setNewGuessCaseSensitive(false);
    setNewGuessMode('multiple');
  };

  const resetGuessQuestionForm = () => {
    setGuessQuestionText('');
    setGuessCorrectAnswers(['']);
    setGuessQuestionCaseSensitive(false);
  };

  const addGuessQuestion = async (gameId: string) => {
    if (!guessQuestionText.trim() || guessCorrectAnswers.every(a => !a.trim())) {
      setError('Question text and at least one correct answer are required');
      return;
    }

    // Filter out empty answers
    const filteredAnswers = guessCorrectAnswers.filter(a => a.trim());

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games/${gameId}/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: guessQuestionText,
          correctAnswers: filteredAnswers,
          caseSensitive: guessQuestionCaseSensitive,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add question');
      }

      await fetchGuessGames();
      setShowNewGuessQuestionForm(null);
      resetGuessQuestionForm();
      setError('');
    } catch (err) {
      console.error('Error adding guess question:', err);
      setError('Failed to add question. Please try again.');
    }
  };

  const updateGuessQuestion = async (gameId: string, questionId: string) => {
    if (!guessQuestionText.trim() || guessCorrectAnswers.every(a => !a.trim())) {
      setError('Question text and at least one correct answer are required');
      return;
    }

    // Filter out empty answers
    const filteredAnswers = guessCorrectAnswers.filter(a => a.trim());

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games/${gameId}/questions/${questionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: guessQuestionText,
          correctAnswers: filteredAnswers,
          caseSensitive: guessQuestionCaseSensitive,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update question');
      }

      await fetchGuessGames();
      setEditingGuessQuestion(null);
      resetGuessQuestionForm();
      setError('');
    } catch (err) {
      console.error('Error updating guess question:', err);
      setError('Failed to update question. Please try again.');
    }
  };

  const deleteGuessQuestion = async (gameId: string, questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/guess-games/${gameId}/questions/${questionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete question');
      }

      await fetchGuessGames();
      setError('');
    } catch (err) {
      console.error('Error deleting guess question:', err);
      setError('Failed to delete question. Please try again.');
    }
  };

  const startEditingGuessQuestion = (question: GuessQuestion) => {
    setEditingGuessQuestion(question.id);
    setGuessQuestionText(question.question);
    setGuessCorrectAnswers(question.correctAnswers.length > 0 ? question.correctAnswers : ['']);
    setGuessQuestionCaseSensitive(question.caseSensitive);
  };

  const cancelEditingGuessQuestion = () => {
    setEditingGuessQuestion(null);
    resetGuessQuestionForm();
  };

  const addGuessCorrectAnswer = () => {
    setGuessCorrectAnswers([...guessCorrectAnswers, '']);
  };

  const removeGuessCorrectAnswer = (index: number) => {
    if (guessCorrectAnswers.length > 1) {
      setGuessCorrectAnswers(guessCorrectAnswers.filter((_, i) => i !== index));
    }
  };

  const updateGuessCorrectAnswer = (index: number, value: string) => {
    const updated = [...guessCorrectAnswers];
    updated[index] = value;
    setGuessCorrectAnswers(updated);
  };

  const addQuestion = async (setId: string) => {
    if (!questionBody.trim() || questionChoices.some(choice => !choice.trim())) {
      setError('All question fields are required');
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/${setId}/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          body: questionBody,
          choices: questionChoices,
          correctIndex: correctChoice,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add question');
      }

      await fetchQuestionSets();
      setShowNewQuestionForm(null);
      resetQuestionForm();
      setError('');
    } catch (err) {
      console.error('Error adding question:', err);
      setError('Failed to add question. Please try again.');
    }
  };

  const updateQuestion = async (setId: string, questionId: string) => {
    if (!questionBody.trim() || questionChoices.some(choice => !choice.trim())) {
      setError('All question fields are required');
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/${setId}/questions/${questionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          body: questionBody,
          choices: questionChoices,
          correctIndex: correctChoice,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update question');
      }

      await fetchQuestionSets();
      setEditingQuestion(null);
      resetQuestionForm();
      setError('');
    } catch (err) {
      console.error('Error updating question:', err);
      setError('Failed to update question. Please try again.');
    }
  };

  const deleteQuestion = async (setId: string, questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/admin/question-sets/${setId}/questions/${questionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete question');
      }

      await fetchQuestionSets();
      setError('');
    } catch (err) {
      console.error('Error deleting question:', err);
      setError('Failed to delete question. Please try again.');
    }
  };

  const startEditingQuestion = (question: Question) => {
    setEditingQuestion(question.id);
    setQuestionBody(question.body);
    setQuestionChoices(question.choices);
    setCorrectChoice(question.correctIndex);
  };

  const cancelEditingQuestion = () => {
    setEditingQuestion(null);
    resetQuestionForm();
  };

  const resetQuestionForm = () => {
    setQuestionBody('');
    setQuestionChoices(['', '', '', '']);
    setCorrectChoice(0);
  };

  const updateChoiceValue = (index: number, value: string) => {
    const newChoices = [...questionChoices];
    newChoices[index] = value;
    setQuestionChoices(newChoices);
  };

  const resetPollForm = () => {
    setNewPollTitle('');
    setNewPollQuestion('');
    setNewPollOptions([
      { id: '1', emoji: '👍', label: 'Yes' },
      { id: '2', emoji: '👎', label: 'No' }
    ]);
    setNewPollStayActive(false);
  };

  const addPollOption = () => {
    const newId = (newPollOptions.length + 1).toString();
    setNewPollOptions([...newPollOptions, { id: newId, emoji: '', label: '' }]);
  };

  const removePollOption = (index: number) => {
    if (newPollOptions.length > 2) {
      setNewPollOptions(newPollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index: number, field: 'emoji' | 'label', value: string) => {
    const updated = [...newPollOptions];
    updated[index] = { ...updated[index], [field]: value };
    setNewPollOptions(updated);
  };

  const renderStateButton = (currentState: StateType, onStateChange: (newState: StateType) => void) => {
    // Guard against undefined currentState
    if (!currentState) {
      return null;
    }

    const states: StateType[] = ['open', 'active', 'closed'];
    const currentIndex = states.indexOf(currentState);
    const nextState = states[(currentIndex + 1) % states.length];
    const StateIcon = stateIcons[currentState];

    return (
      <div className="flex items-center gap-2">
        <Badge className={stateColors[currentState]}>
          <StateIcon className="w-3 h-3 mr-1" />
          {currentState.charAt(0).toUpperCase() + currentState.slice(1)}
        </Badge>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onStateChange(nextState)}
        >
          Set to {nextState.charAt(0).toUpperCase() + nextState.slice(1)}
        </Button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-3xl font-bold mb-2">🛠️ Admin Dashboard</CardTitle>
                <CardDescription className="text-lg">
                  Manage trivia question sets and polls with state controls.
                </CardDescription>
              </div>
              <Button 
                onClick={() => window.history.back()}
                variant="secondary"
                size="sm"
              >
                Back to Lobby
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Error Message */}
        {error && (
          <Card className="mb-6 border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex">
                <div className="text-destructive">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-destructive">Error</h3>
                  <p className="text-sm text-destructive/80 mt-1">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for Trivia, Polls, Guess the Value, and Notifications */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="trivia">Trivia</TabsTrigger>
            <TabsTrigger value="polls">Polls</TabsTrigger>
            <TabsTrigger value="guess">Guess the Value</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
          </TabsList>

          {/* Trivia Tab */}
          <TabsContent value="trivia" className="space-y-6">
            {/* Create New Question Set */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlusCircle className="w-5 h-5" />
                  Create New Question Set
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!showNewSetForm ? (
                  <Button onClick={() => setShowNewSetForm(true)}>
                    Add Question Set
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="new-title">Title</Label>
                      <Input
                        id="new-title"
                        value={newSetTitle}
                        onChange={(e) => setNewSetTitle(e.target.value)}
                        placeholder="Enter question set title..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-description">Description</Label>
                      <Textarea
                        id="new-description"
                        value={newSetDescription}
                        onChange={(e) => setNewSetDescription(e.target.value)}
                        placeholder="Enter description (optional)..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-game-mode">Game Mode</Label>
                      <Select value={newSetGameMode} onValueChange={(value: 'synchronized' | 'self-paced') => setNewSetGameMode(value)}>
                        <SelectTrigger id="new-game-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="synchronized">Synchronized - All players wait for each other</SelectItem>
                          <SelectItem value="self-paced">Self-Paced - Players advance independently</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground mt-1">
                        {newSetGameMode === 'synchronized' 
                          ? 'All players must answer each question before advancing to the next' 
                          : 'Each player can progress through questions at their own pace'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={createQuestionSet}>
                        <Save className="w-4 h-4 mr-2" />
                        Create
                      </Button>
                      <Button 
                        variant="secondary" 
                        onClick={() => {
                          setShowNewSetForm(false);
                          setNewSetTitle('');
                          setNewSetDescription('');
                          setNewSetGameMode('synchronized');
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Question Sets List */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleQuestionSetDragEnd}
            >
              <SortableContext
                items={questionSets.map((qs) => qs.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-6">
                  {questionSets.map((questionSet) => (
                    <SortableCard key={questionSet.id} id={questionSet.id}>
                      <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {editingSet === questionSet.id ? (
                          <div className="space-y-6">
                            <div className="space-y-4">
                              <div>
                                <Label htmlFor="edit-title">Title</Label>
                                <Input
                                  id="edit-title"
                                  value={editSetTitle}
                                  onChange={(e) => setEditSetTitle(e.target.value)}
                                />
                              </div>
                              <div>
                                <Label htmlFor="edit-description">Description</Label>
                                <Textarea
                                  id="edit-description"
                                  value={editSetDescription}
                                  onChange={(e) => setEditSetDescription(e.target.value)}
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button onClick={() => updateQuestionSet(questionSet.id)}>
                                  <Save className="w-4 h-4 mr-2" />
                                  Save Details
                                </Button>
                                <Button variant="secondary" onClick={cancelEditingSet}>
                                  <X className="w-4 h-4 mr-2" />
                                  Cancel
                                </Button>
                              </div>
                            </div>

                            {/* Questions Management */}
                            <div className="border-t pt-6">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold">Questions</h4>
                                <Button 
                                  size="sm"
                                  onClick={() => setShowNewQuestionForm(questionSet.id)}
                                >
                                  <PlusCircle className="w-4 h-4 mr-2" />
                                  Add Question
                                </Button>
                              </div>

                              {/* Add New Question Form */}
                              {showNewQuestionForm === questionSet.id && (
                                <Card className="border-dashed mb-4">
                                  <CardContent className="pt-6">
                                    <div className="space-y-4">
                                      <div>
                                        <Label htmlFor="question-body">Question</Label>
                                        <Textarea
                                          id="question-body"
                                          value={questionBody}
                                          onChange={(e) => setQuestionBody(e.target.value)}
                                          placeholder="Enter your question..."
                                        />
                                      </div>
                                      <div>
                                        <Label>Answer Choices</Label>
                                        <div className="space-y-2">
                                          {questionChoices.map((choice, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                              <input
                                                type="radio"
                                                name="correct-choice"
                                                checked={correctChoice === index}
                                                onChange={() => setCorrectChoice(index)}
                                                className="w-4 h-4"
                                              />
                                              <Input
                                                value={choice}
                                                onChange={(e) => updateChoiceValue(index, e.target.value)}
                                                placeholder={`Choice ${index + 1}`}
                                              />
                                              <span className="text-sm text-muted-foreground min-w-[80px]">
                                                {correctChoice === index ? '(Correct)' : ''}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="flex gap-2">
                                        <Button onClick={() => addQuestion(questionSet.id)}>
                                          <Save className="w-4 h-4 mr-2" />
                                          Add Question
                                        </Button>
                                        <Button 
                                          variant="secondary" 
                                          onClick={() => {
                                            setShowNewQuestionForm(null);
                                            resetQuestionForm();
                                          }}
                                        >
                                          <X className="w-4 h-4 mr-2" />
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              )}

                              {/* Existing Questions */}
                              {questionSet.questions && questionSet.questions.length > 0 ? (
                                <div className="space-y-3">
                                  {questionSet.questions.map((question, index) => (
                                    <Card key={question.id} className="border-l-4 border-l-primary">
                                      <CardContent className="pt-4">
                                        {editingQuestion === question.id ? (
                                          <div className="space-y-4">
                                            <div>
                                              <Label htmlFor="edit-question-body">Question</Label>
                                              <Textarea
                                                id="edit-question-body"
                                                value={questionBody}
                                                onChange={(e) => setQuestionBody(e.target.value)}
                                                placeholder="Enter your question..."
                                              />
                                            </div>
                                            <div>
                                              <Label>Answer Choices</Label>
                                              <div className="space-y-2">
                                                {questionChoices.map((choice, choiceIndex) => (
                                                  <div key={choiceIndex} className="flex items-center gap-2">
                                                    <input
                                                      type="radio"
                                                      name="edit-correct-choice"
                                                      checked={correctChoice === choiceIndex}
                                                      onChange={() => setCorrectChoice(choiceIndex)}
                                                      className="w-4 h-4"
                                                    />
                                                    <Input
                                                      value={choice}
                                                      onChange={(e) => updateChoiceValue(choiceIndex, e.target.value)}
                                                      placeholder={`Choice ${choiceIndex + 1}`}
                                                    />
                                                    <span className="text-sm text-muted-foreground min-w-[80px]">
                                                      {correctChoice === choiceIndex ? '(Correct)' : ''}
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                            <div className="flex gap-2">
                                              <Button onClick={() => updateQuestion(questionSet.id, question.id)}>
                                                <Save className="w-4 h-4 mr-2" />
                                                Save Question
                                              </Button>
                                              <Button variant="secondary" onClick={cancelEditingQuestion}>
                                                <X className="w-4 h-4 mr-2" />
                                                Cancel
                                              </Button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                              <h5 className="font-medium mb-2">
                                                {index + 1}. {question.body}
                                              </h5>
                                              <ul className="space-y-1">
                                                {question.choices.map((choice, choiceIndex) => (
                                                  <li 
                                                    key={choiceIndex}
                                                    className={`text-sm ${
                                                      choiceIndex === question.correctIndex 
                                                        ? 'text-green-600 font-medium' 
                                                        : 'text-muted-foreground'
                                                    }`}
                                                  >
                                                    {String.fromCharCode(65 + choiceIndex)}. {choice}
                                                    {choiceIndex === question.correctIndex && ' ✓'}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                            <div className="flex gap-2">
                                              <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => startEditingQuestion(question)}
                                              >
                                                <Edit className="w-4 h-4" />
                                              </Button>
                                              <Button 
                                                variant="destructive" 
                                                size="sm"
                                                onClick={() => deleteQuestion(questionSet.id, question.id)}
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted-foreground text-center py-8">
                                  No questions added yet. Click "Add Question" to get started.
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-3 mb-2">
                              <CardTitle className="text-xl">{questionSet.title}</CardTitle>
                              {renderStateButton(questionSet.state, (newState) => updateQuestionSetState(questionSet.id, newState))}
                            </div>
                            <CardDescription>{questionSet.description}</CardDescription>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge variant={questionSet.gameMode === 'synchronized' ? 'secondary' : 'outline'} className="text-xs">
                                {questionSet.gameMode === 'synchronized' ? '🔄 Synchronized' : '🏃 Self-Paced'}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {questionSet.questionCount} questions
                              </Badge>
                            </div>
                          </>
                        )}
                      </div>
                      {editingSet !== questionSet.id && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => startEditingSet(questionSet)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => duplicateQuestionSet(questionSet.id)}
                            title="Duplicate question set"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => deleteQuestionSet(questionSet.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
                    </SortableCard>
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Empty State */}
            {questionSets.length === 0 && (
              <Card>
                <CardContent className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No trivia found</h3>
                  <p className="text-muted-foreground">Create your first trivia set to get started.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Polls Tab */}
          <TabsContent value="polls" className="space-y-6">
            {/* Create New Poll */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlusCircle className="w-5 h-5" />
                  Create New Poll
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!showNewPollForm ? (
                  <Button onClick={() => setShowNewPollForm(true)}>
                    Add Poll
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="poll-title">Title</Label>
                      <Input
                        id="poll-title"
                        value={newPollTitle}
                        onChange={(e) => setNewPollTitle(e.target.value)}
                        placeholder="Enter poll title..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="poll-question">Question</Label>
                      <Textarea
                        id="poll-question"
                        value={newPollQuestion}
                        onChange={(e) => setNewPollQuestion(e.target.value)}
                        placeholder="Enter poll question..."
                      />
                    </div>
                    <div>
                      <Label>Poll Options</Label>
                      <div className="space-y-2">
                        {newPollOptions.map((option, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={option.emoji}
                              onChange={(e) => updatePollOption(index, 'emoji', e.target.value)}
                              placeholder="🔥"
                              className="w-20"
                            />
                            <Input
                              value={option.label || ''}
                              onChange={(e) => updatePollOption(index, 'label', e.target.value)}
                              placeholder={`Option ${index + 1} (optional)`}
                              className="flex-1"
                            />
                            {newPollOptions.length > 2 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removePollOption(index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addPollOption}
                        >
                          <PlusCircle className="w-4 h-4 mr-2" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="stay-active"
                        checked={newPollStayActive}
                        onCheckedChange={setNewPollStayActive}
                      />
                      <Label htmlFor="stay-active">Keep poll active indefinitely</Label>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={createPoll}>
                        <Save className="w-4 h-4 mr-2" />
                        Create Poll
                      </Button>
                      <Button 
                        variant="secondary" 
                        onClick={() => {
                          setShowNewPollForm(false);
                          resetPollForm();
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Polls List */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handlePollDragEnd}
            >
              <SortableContext
                items={polls.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-6">
                  {polls.map((poll) => (
                    <SortableCard key={poll.id} id={poll.id}>
                      <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {editingPoll === poll.id ? (
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="edit-poll-title">Title</Label>
                              <Input
                                id="edit-poll-title"
                                value={newPollTitle}
                                onChange={(e) => setNewPollTitle(e.target.value)}
                                placeholder="Enter poll title..."
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-poll-question">Question</Label>
                              <Textarea
                                id="edit-poll-question"
                                value={newPollQuestion}
                                onChange={(e) => setNewPollQuestion(e.target.value)}
                                placeholder="Enter poll question..."
                              />
                            </div>
                            <div>
                              <Label>Poll Options</Label>
                              <div className="space-y-2">
                                {newPollOptions.map((option, index) => (
                                  <div key={index} className="flex items-center gap-2">
                                    <Input
                                      value={option.emoji}
                                      onChange={(e) => updatePollOption(index, 'emoji', e.target.value)}
                                      placeholder="🔥"
                                      className="w-20"
                                    />
                                    <Input
                                      value={option.label || ''}
                                      onChange={(e) => updatePollOption(index, 'label', e.target.value)}
                                      placeholder={`Option ${index + 1} (optional)`}
                                      className="flex-1"
                                    />
                                    {newPollOptions.length > 2 && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => removePollOption(index)}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={addPollOption}
                                >
                                  <PlusCircle className="w-4 h-4 mr-2" />
                                  Add Option
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="edit-stay-active"
                                checked={newPollStayActive}
                                onCheckedChange={setNewPollStayActive}
                              />
                              <Label htmlFor="edit-stay-active">Keep poll active indefinitely</Label>
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={() => updatePoll(poll.id)}>
                                <Save className="w-4 h-4 mr-2" />
                                Save Changes
                              </Button>
                              <Button variant="secondary" onClick={cancelEditingPoll}>
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-3 mb-2">
                              <CardTitle className="text-xl">{poll.title}</CardTitle>
                              {renderStateButton(poll.state, (newState) => updatePollState(poll.id, newState))}
                              {poll.stayActive && (
                                <Badge variant="secondary">
                                  <Pause className="w-3 h-3 mr-1" />
                                  Stays Active
                                </Badge>
                              )}
                            </div>
                            <CardDescription className="mb-3">{poll.question}</CardDescription>
                            <div className="flex flex-wrap gap-2">
                              {poll.options.map((option) => (
                                <Badge key={option.id} variant="outline">
                                  {option.emoji} {option.label}
                                </Badge>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {editingPoll !== poll.id && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => startEditingPoll(poll)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => duplicatePoll(poll.id)}
                            title="Duplicate poll"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => deletePoll(poll.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
                    </SortableCard>
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {polls.length === 0 && (
              <Card>
                <CardContent className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No polls found</h3>
                  <p className="text-muted-foreground">Create your first poll to get started.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Guess the Value Tab */}
          <TabsContent value="guess" className="space-y-6">
            {/* Create New Guess Game */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlusCircle className="w-5 h-5" />
                  Create New Guess the Value
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!showNewGuessForm ? (
                  <Button onClick={() => setShowNewGuessForm(true)}>
                    Add Guess Game
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="guess-title">Title</Label>
                      <Input
                        id="guess-title"
                        value={newGuessTitle}
                        onChange={(e) => setNewGuessTitle(e.target.value)}
                        placeholder="Enter game title..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="guess-description">Description</Label>
                      <Textarea
                        id="guess-description"
                        value={newGuessDescription}
                        onChange={(e) => setNewGuessDescription(e.target.value)}
                        placeholder="Enter description (optional)..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="guess-mode">Game Mode</Label>
                      <Select value={newGuessMode} onValueChange={(value: 'multiple' | 'collect') => setNewGuessMode(value)}>
                        <SelectTrigger id="guess-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="multiple">Multiple Guess Mode (Unlimited attempts)</SelectItem>
                          <SelectItem value="collect">Collect Answer Mode (One attempt only)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground mt-1">
                        {newGuessMode === 'multiple' 
                          ? 'Participants can guess multiple times until they get it right' 
                          : 'Participants can only submit one answer'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={createGuessGame}>
                        <Save className="w-4 h-4 mr-2" />
                        Create Game
                      </Button>
                      <Button 
                        variant="secondary" 
                        onClick={() => {
                          setShowNewGuessForm(false);
                          resetGuessForm();
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Guess Games List */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleGuessGameDragEnd}
            >
              <SortableContext
                items={guessGames.map((g) => g.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-6">
                  {guessGames.map((game) => (
                    <SortableCard key={game.id} id={game.id}>
                      <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {editingGuessGame === game.id ? (
                          <div className="space-y-6">
                            <div className="flex items-center gap-3 mb-2">
                              <CardTitle className="text-xl">{game.title}</CardTitle>
                              {renderStateButton(game.state, (newState) => updateGuessGameState(game.id, newState))}
                            </div>
                            <CardDescription className="mb-3">{game.description}</CardDescription>
                            <div className="flex flex-wrap gap-2 mb-4">
                              <Badge variant="secondary">
                                {game.mode === 'multiple' ? 'Multiple Guesses' : 'One Attempt'}
                              </Badge>
                              <Badge variant="outline">
                                {game.questionCount || 0} questions
                              </Badge>
                            </div>

                            {/* Questions Management */}
                            <div className="border-t pt-6">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold">Questions</h4>
                                <Button 
                                  size="sm"
                                  onClick={() => setShowNewGuessQuestionForm(game.id)}
                                >
                                  <PlusCircle className="w-4 h-4 mr-2" />
                                  Add Question
                                </Button>
                              </div>

                              {/* Add New Question Form */}
                              {showNewGuessQuestionForm === game.id && (
                                <Card className="border-dashed mb-4">
                                  <CardContent className="pt-6">
                                    <div className="space-y-4">
                                      <div>
                                        <Label htmlFor="guess-question-text">Question</Label>
                                        <Textarea
                                          id="guess-question-text"
                                          value={guessQuestionText}
                                          onChange={(e) => setGuessQuestionText(e.target.value)}
                                          placeholder="Enter your question..."
                                        />
                                      </div>
                                      <div>
                                        <Label>Correct Answers (players can submit any of these)</Label>
                                        <div className="space-y-2">
                                          {guessCorrectAnswers.map((answer, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                              <Input
                                                value={answer}
                                                onChange={(e) => updateGuessCorrectAnswer(index, e.target.value)}
                                                placeholder={`Correct answer ${index + 1}`}
                                                className="flex-1"
                                              />
                                              {guessCorrectAnswers.length > 1 && (
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => removeGuessCorrectAnswer(index)}
                                                >
                                                  <X className="w-4 h-4" />
                                                </Button>
                                              )}
                                            </div>
                                          ))}
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={addGuessCorrectAnswer}
                                          >
                                            <PlusCircle className="w-4 h-4 mr-2" />
                                            Add Another Answer
                                          </Button>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <Switch
                                          id="guess-question-case-sensitive"
                                          checked={guessQuestionCaseSensitive}
                                          onCheckedChange={setGuessQuestionCaseSensitive}
                                        />
                                        <Label htmlFor="guess-question-case-sensitive">Case Sensitive</Label>
                                      </div>
                                      <div className="flex gap-2">
                                        <Button onClick={() => addGuessQuestion(game.id)}>
                                          <Save className="w-4 h-4 mr-2" />
                                          Add Question
                                        </Button>
                                        <Button 
                                          variant="secondary" 
                                          onClick={() => {
                                            setShowNewGuessQuestionForm(null);
                                            resetGuessQuestionForm();
                                          }}
                                        >
                                          <X className="w-4 h-4 mr-2" />
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              )}

                              {/* Existing Questions */}
                              {game.questions && game.questions.length > 0 ? (
                                <div className="space-y-3">
                                  {game.questions.map((question, index) => (
                                    <Card key={question.id} className="border-l-4 border-l-primary">
                                      <CardContent className="pt-4">
                                        {editingGuessQuestion === question.id ? (
                                          <div className="space-y-4">
                                            <div>
                                              <Label htmlFor="edit-guess-question-text">Question</Label>
                                              <Textarea
                                                id="edit-guess-question-text"
                                                value={guessQuestionText}
                                                onChange={(e) => setGuessQuestionText(e.target.value)}
                                                placeholder="Enter your question..."
                                              />
                                            </div>
                                            <div>
                                              <Label>Correct Answers</Label>
                                              <div className="space-y-2">
                                                {guessCorrectAnswers.map((answer, answerIndex) => (
                                                  <div key={answerIndex} className="flex items-center gap-2">
                                                    <Input
                                                      value={answer}
                                                      onChange={(e) => updateGuessCorrectAnswer(answerIndex, e.target.value)}
                                                      placeholder={`Correct answer ${answerIndex + 1}`}
                                                      className="flex-1"
                                                    />
                                                    {guessCorrectAnswers.length > 1 && (
                                                      <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => removeGuessCorrectAnswer(answerIndex)}
                                                      >
                                                        <X className="w-4 h-4" />
                                                      </Button>
                                                    )}
                                                  </div>
                                                ))}
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={addGuessCorrectAnswer}
                                                >
                                                  <PlusCircle className="w-4 h-4 mr-2" />
                                                  Add Another Answer
                                                </Button>
                                              </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <Switch
                                                id="edit-guess-question-case-sensitive"
                                                checked={guessQuestionCaseSensitive}
                                                onCheckedChange={setGuessQuestionCaseSensitive}
                                              />
                                              <Label htmlFor="edit-guess-question-case-sensitive">Case Sensitive</Label>
                                            </div>
                                            <div className="flex gap-2">
                                              <Button onClick={() => updateGuessQuestion(game.id, question.id)}>
                                                <Save className="w-4 h-4 mr-2" />
                                                Save Question
                                              </Button>
                                              <Button variant="secondary" onClick={cancelEditingGuessQuestion}>
                                                <X className="w-4 h-4 mr-2" />
                                                Cancel
                                              </Button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                              <h5 className="font-medium mb-2">
                                                {index + 1}. {question.question}
                                              </h5>
                                              <div className="space-y-1">
                                                <p className="text-sm font-semibold text-green-600">Correct Answers:</p>
                                                {question.correctAnswers.map((answer, answerIndex) => (
                                                  <p key={answerIndex} className="text-sm text-muted-foreground ml-4">
                                                    • {answer}
                                                  </p>
                                                ))}
                                              </div>
                                              {question.caseSensitive && (
                                                <Badge variant="secondary" className="mt-2">
                                                  Case Sensitive
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="flex gap-2">
                                              <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => startEditingGuessQuestion(question)}
                                              >
                                                <Edit className="w-4 h-4" />
                                              </Button>
                                              <Button 
                                                variant="destructive" 
                                                size="sm"
                                                onClick={() => deleteGuessQuestion(game.id, question.id)}
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted-foreground text-center py-8">
                                  No questions added yet. Click "Add Question" to get started.
                                </p>
                              )}
                            </div>

                            <div className="flex gap-2 mt-4">
                              <Button 
                                variant="secondary"
                                onClick={() => setEditingGuessGame(null)}
                              >
                                Done Editing
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-3 mb-2">
                              <CardTitle className="text-xl">{game.title}</CardTitle>
                              {renderStateButton(game.state, (newState) => updateGuessGameState(game.id, newState))}
                            </div>
                            <CardDescription className="mb-3">{game.description}</CardDescription>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="secondary">
                                {game.mode === 'multiple' ? 'Multiple Guesses' : 'One Attempt'}
                              </Badge>
                              <Badge variant="outline">
                                {game.questionCount || 0} questions
                              </Badge>
                            </div>
                          </>
                        )}
                      </div>
                      {editingGuessGame !== game.id && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingGuessGame(game.id)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => duplicateGuessGame(game.id)}
                            title="Duplicate guess game"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => deleteGuessGame(game.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
                    </SortableCard>
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {guessGames.length === 0 && (
              <Card>
                <CardContent className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No guess games found</h3>
                  <p className="text-muted-foreground">Create your first guess game to get started.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <NotificationManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default AdminDashboard;